"use client";

import { useEffect, useRef } from "react";
import { create } from "zustand";
import {
  useInitializationStatus,
  useLDClient,
  type LDContext,
} from "@launchdarkly/react-sdk";
import { useClaritySession, type SessionState } from "@/lib/auth/auth-store";
import { getSessionEmail } from "@/lib/auth/session-identity";
import { buildAnonymousLaunchDarklyContext } from "./LaunchDarklyProvider";

type IdentityTarget = {
  context: LDContext;
  signature: string;
};

let activeIdentityBridges = 0;
let anonymousResetGeneration = 0;
// The client and settled state are shared across bridge remounts and resets.
let identifyAttemptGeneration = 0;

// The last identify() that settled. Until the current session's identify
// settles, flag values still belong to the previous context.
const useSettledIdentity = create<{
  signature: string | null;
  completed: boolean;
}>(() => ({ signature: null, completed: false }));

function markIdentitySettled(signature: string, completed: boolean) {
  useSettledIdentity.setState({ signature, completed });
}

function beginIdentityAttempt() {
  const generation = ++identifyAttemptGeneration;
  useSettledIdentity.setState({ signature: null, completed: false });
  return generation;
}

export function __resetLaunchDarklyIdentityForTests(): void {
  beginIdentityAttempt();
}

function getEnvironment() {
  return process.env.NEXT_PUBLIC_ENVIRONMENT ?? "development";
}

function canSynchronizeIdentity() {
  return (
    typeof window !== "undefined" &&
    window.__E2E_TEST_MODE__ !== true &&
    Boolean(process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID?.trim())
  );
}

function getClaimString(
  claims: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = claims[key];

  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
}

function createIdentityTarget(session: SessionState): IdentityTarget | null {
  if (session.status === "loading") return null;

  if (session.status === "authenticated") {
    const userKey = getClaimString(session.claims, "sub");

    if (userKey) {
      const email = getSessionEmail(session.claims);
      const context: LDContext = {
        kind: "user",
        key: userKey,
        ...(email ? { email } : {}),
        custom: { environment: getEnvironment() },
      };

      return { context, signature: JSON.stringify(context) };
    }
  }

  const context = buildAnonymousLaunchDarklyContext();
  return { context, signature: JSON.stringify(context) };
}

/**
 * Keeps the singleton LaunchDarkly client synchronized with the authenticated
 * ClarityOS session without recreating the provider or its streaming client.
 */
export function LaunchDarklyIdentityBridge() {
  const session = useClaritySession();
  const ldClient = useLDClient();
  const initialization = useInitializationStatus();
  const attemptedIdentityRef = useRef<string | null>(null);
  const canIdentify =
    initialization.status === "complete" && canSynchronizeIdentity();

  useEffect(() => {
    const target = createIdentityTarget(session);

    if (
      !ldClient ||
      !canIdentify ||
      !target ||
      attemptedIdentityRef.current === target.signature
    ) {
      return;
    }

    attemptedIdentityRef.current = target.signature;
    const generation = beginIdentityAttempt();

    void ldClient
      .identify(target.context)
      .then((result) => {
        // The SDK can shed queued calls, but an already running call still
        // settles. A generation also distinguishes the two A calls in A→B→A.
        if (
          result.status === "shed" ||
          generation !== identifyAttemptGeneration
        ) {
          return;
        }

        // Settle even on failure so flag-gated consumers stop waiting and
        // treat LaunchDarkly as unavailable.
        markIdentitySettled(target.signature, result.status === "completed");
        if (result.status === "completed") return;

        console.error(
          "LaunchDarkly identity update did not complete; it will retry after the next session transition.",
          result,
        );

        attemptedIdentityRef.current = null;
      })
      .catch((error: unknown) => {
        // Defensive: the v4 browser SDK documents that identify never rejects.
        if (generation !== identifyAttemptGeneration) return;
        console.error(
          "LaunchDarkly identity update failed unexpectedly.",
          error,
        );

        markIdentitySettled(target.signature, false);
        attemptedIdentityRef.current = null;
      });
  }, [canIdentify, ldClient, session]);

  useEffect(() => {
    if (!ldClient || !canIdentify) return;

    activeIdentityBridges += 1;
    anonymousResetGeneration += 1;

    return () => {
      activeIdentityBridges -= 1;
      const resetGeneration = ++anonymousResetGeneration;
      const anonymousContext = buildAnonymousLaunchDarklyContext();
      const anonymousSignature = JSON.stringify(anonymousContext);

      if (attemptedIdentityRef.current === anonymousSignature) return;

      // Defer by one microtask so React Strict Mode's effect replay, or an
      // immediate authenticated-route remount, can cancel this reset.
      queueMicrotask(() => {
        if (
          activeIdentityBridges > 0 ||
          anonymousResetGeneration !== resetGeneration
        ) {
          return;
        }

        const generation = beginIdentityAttempt();
        void ldClient
          .identify(anonymousContext)
          .then((result) => {
            if (
              result.status === "shed" ||
              generation !== identifyAttemptGeneration
            ) {
              return;
            }

            markIdentitySettled(
              anonymousSignature,
              result.status === "completed",
            );
            if (result.status === "completed") return;

            console.error(
              "LaunchDarkly anonymous identity reset did not complete.",
              result,
            );
          })
          .catch((error: unknown) => {
            if (generation !== identifyAttemptGeneration) return;
            console.error(
              "LaunchDarkly anonymous identity reset failed unexpectedly.",
              error,
            );
            markIdentitySettled(anonymousSignature, false);
          });
      });
    };
  }, [canIdentify, ldClient]);

  return null;
}

export type LaunchDarklyIdentityStatus =
  | "pending"
  | "identified"
  | "unavailable";

/**
 * Whether flag values belong to the current session's LaunchDarkly context.
 *
 * - `pending`: the session or its identify() has not settled yet.
 * - `identified`: identify() completed for this session; flags are live.
 * - `unavailable`: no live client (E2E, missing configuration, or a failed or
 *   timed-out start) or identify() failed. Flag values may be defaults or a
 *   cached context's, so identity-sensitive features should stay off.
 *
 * `LaunchDarklyGate` only renders the app while initializing when no live
 * client exists, so a non-complete status here always means there is none.
 */
export function useLaunchDarklyIdentityStatus(): LaunchDarklyIdentityStatus {
  const session = useClaritySession();
  const initialization = useInitializationStatus();
  const settledSignature = useSettledIdentity((state) => state.signature);
  const settledCompleted = useSettledIdentity((state) => state.completed);

  if (session.status === "loading") return "pending";
  if (initialization.status !== "complete" || !canSynchronizeIdentity()) {
    return "unavailable";
  }

  const target = createIdentityTarget(session);
  if (!target || target.signature !== settledSignature) return "pending";

  return settledCompleted ? "identified" : "unavailable";
}
